/**
 * DropsList
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import type { SmswiserDrop } from '@/types/smswiser-drops';
import DropsPagination from '@/features/drops/components/DropsPagination';
import DropsListHeader from '@/features/drops/components/DropsListHeader';
import DropsRow from '@/features/drops/components/DropsRow';
import type { DropsSortDir, DropsSortKey } from '@/features/drops/utils/sort';

interface DropsListProps {
  drops: SmswiserDrop[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** When set, drop links pass this so Back returns here (e.g. /smswiser/australia?tab=deployed) */
  returnTo?: string;
  sortBy: DropsSortKey;
  sortDir: DropsSortDir;
  onSort: (key: DropsSortKey) => void;
}

export const DropsList: FC<DropsListProps> = ({
  drops,
  page,
  totalPages,
  onPageChange,
  returnTo,
  sortBy,
  sortDir,
  onSort,
}) => {
  const prefersReducedMotion = useReducedMotion();

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: prefersReducedMotion ? 0 : 0.03 },
    },
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="divide-y divide-border rounded-lg border border-border overflow-hidden"
    >
      <DropsListHeader sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      <AnimatePresence initial={false}>
        {drops.map((drop) => (
          <DropsRow key={drop.uniqueId} drop={drop} returnTo={returnTo} />
        ))}
      </AnimatePresence>

      <DropsPagination
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </motion.div>
  );
};

export default DropsList;

